import type { ProductVariant } from '@/api/endpoints'

export const toPriceNumber = (value: number | string | null | undefined): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value !== 'string') return 0

  const normalized = value.replace(/\s/g, '').replace(',', '.')
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : 0
}

export const getVariantBasePrice = (variant?: ProductVariant | null): number =>
  toPriceNumber(variant?.price)

export const getVariantFinalPrice = (variant?: ProductVariant | null): number => {
  if (!variant) return 0
  return variant.final_price != null
    ? toPriceNumber(variant.final_price)
    : getVariantBasePrice(variant)
}

export const getVariantDiscountPercent = (
  variant?: ProductVariant | null,
): number | null => {
  if (!variant) return null

  const explicitPercent = toPriceNumber(variant.discount_percent)
  if (explicitPercent > 0) return explicitPercent

  const basePrice = getVariantBasePrice(variant)
  const finalPrice = getVariantFinalPrice(variant)

  if (basePrice > 0 && finalPrice > 0 && basePrice > finalPrice) {
    return ((basePrice - finalPrice) / basePrice) * 100
  }

  return null
}

export const variantHasDiscount = (variant?: ProductVariant | null): boolean => {
  if (!variant) return false

  if (getVariantDiscountPercent(variant) != null) return true
  if (toPriceNumber(variant.discount_amount) > 0) return true

  return getVariantBasePrice(variant) > getVariantFinalPrice(variant)
}

export const getVariantOriginalPrice = (
  variant?: ProductVariant | null,
): number | null => {
  if (!variantHasDiscount(variant)) return null
  const basePrice = getVariantBasePrice(variant)
  return basePrice > 0 ? basePrice : null
}

export const sortVariantsByPosition = (
  variants: ProductVariant[] | undefined | null,
): ProductVariant[] =>
  variants?.length
    ? [...variants].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    : []

export const selectPreferredCardVariant = (
  variants: ProductVariant[] | undefined | null,
): ProductVariant | undefined => {
  const sorted = sortVariantsByPosition(variants)
  if (!sorted.length) return undefined

  return (
    sorted.find((variant) => (variant.available_quantity ?? 0) > 0 && variantHasDiscount(variant)) ??
    sorted.find((variant) => variantHasDiscount(variant)) ??
    sorted.find((variant) => (variant.available_quantity ?? 0) > 0) ??
    sorted[0]
  )
}
